import React, {useEffect, useState} from "react";
import {Box, Stack, Typography, IconButton, Button, Modal, Fade, InputBase, Chip, MenuItem, Select, CircularProgress} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon} from "@hugeicons/core-free-icons";
import {useAtomValue} from "jotai";
import {toast} from "react-toastify";
import {KnowledgeManagerRepository} from "../../../../repositories/KnowledgeManagerRepository";
import {SubProjectOption} from "../../SubProjectSelect";
import {projectAtom, subProjectAtom} from "../atoms";

const COLORS = ["#8491F7", "#6366f1", "#8b5cf6", "#ec4899", "#f43f5e", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3D86C6"];

const controlSx = {
  height: 40, display: "flex", alignItems: "center", gap: 1, px: 1.25, borderRadius: "10px",
  bgcolor: "#FAFAFD", color: "#6B6B80", fontSize: 13,
};

const labelSx = {fontSize: 12, fontWeight: 600, color: '#6B6B80', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em'};

const initForm = {name: "", description: "", color: COLORS[0], tags: [], tagInput: "", subProjectId: ""};

export const CreateTopicModalNew = ({open, onClose, onCreated}) => {
  const project = useAtomValue(projectAtom);
  const currentSubProject = useAtomValue(subProjectAtom);
  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const defaultLanguageId = currentSubProject?.id ?? project?.subProjects?.[0]?.id;
      setForm({...initForm, subProjectId: defaultLanguageId ? String(defaultLanguageId) : ""});
    }
  }, [open, currentSubProject, project?.subProjects]);

  const onChange = (key, value) => setForm(prev => ({...prev, [key]: value}));

  const handleAddTag = () => {
    const tag = form.tagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag)) {
      setForm(prev => ({...prev, tags: [...prev.tags, tag], tagInput: ""}));
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subProjectId || !project?.id) return;
    setSaving(true);
    try {
      const topic = await KnowledgeManagerRepository.createTopic({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        tags: form.tags,
        color: form.color,
        organization: project.organizationId,
        projectId: String(project.id),
        subProjectId: form.subProjectId,
      });
      toast.success(`Topic "${topic.name}" created`);
      onCreated?.(topic);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create topic");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Fade in={open}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: {xs: "calc(100% - 24px)", md: "min(520px, 92vw)"}, bgcolor: '#fff', borderRadius: '20px',
          boxShadow: '0 24px 70px rgba(35, 38, 77, 0.22)', p: {xs: 2.5, md: 3}, outline: 'none',
          maxHeight: "90vh", overflowY: "auto",
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 3}}>
            <Box>
              <Typography sx={{fontSize: 18, fontWeight: 700, color: '#23264D', letterSpacing: '-0.01em'}}>New Topic</Typography>
              <Typography sx={{fontSize: 13, color: '#8A8AA0', mt: 0.5}}>Create a knowledge base topic for this project</Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{color: '#8A8AA0'}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Stack gap={2} sx={{mb: 2.5}}>
            <Box>
              <Typography sx={labelSx}>Topic Name</Typography>
              <Box sx={{...controlSx}}>
                <InputBase
                  placeholder="e.g., Product Support"
                  value={form.name}
                  onChange={e => onChange("name", e.target.value)}
                  inputProps={{"aria-label": "Topic name"}}
                  autoFocus
                  sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
            </Box>

            <Box>
              <Typography sx={labelSx}>Description</Typography>
              <Box sx={{...controlSx, height: "auto", alignItems: "flex-start", py: 1.25}}>
                <InputBase
                  fullWidth multiline minRows={3}
                  value={form.description}
                  onChange={e => onChange("description", e.target.value)}
                  placeholder={"Describe what this knowledge base will cover..."}
                  inputProps={{"aria-label": "Description"}}
                  sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
            </Box>

            {project?.subProjects?.length > 0 && (
              <Box>
                <Typography sx={labelSx}>Sub project</Typography>
                <Select
                  variant="standard"
                  disableUnderline
                  fullWidth
                  value={form.subProjectId}
                  onChange={e => onChange("subProjectId", e.target.value)}
                  renderValue={(selected) => {
                    const match = project.subProjects.find(item => String(item.id) === selected);
                    return match ? <SubProjectOption subProject={match}/> : null;
                  }}
                  inputProps={{"aria-label": "Sub project"}}
                  sx={{
                    ...controlSx,
                    "& .MuiSelect-select": {display: "flex", alignItems: "center", py: 0, pr: "22px !important"},
                    "& .MuiSelect-icon": {color: "#6B6B80"},
                  }}
                  MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
                >
                  {project.subProjects.map(item => (
                    <MenuItem key={item.id} value={String(item.id)} dense>
                      <SubProjectOption subProject={item}/>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            <Box>
              <Typography sx={labelSx}>Color</Typography>
              <Stack direction={"row"} gap={1} flexWrap={"wrap"}>
                {COLORS.map(c => (
                  <Box
                    key={c}
                    onClick={() => onChange("color", c)}
                    sx={{
                      width: 26, height: 26, borderRadius: "50%", bgcolor: c, cursor: "pointer",
                      outline: form.color === c ? "2px solid #23264D" : "none",
                      outlineOffset: "2px",
                      transform: form.color === c ? "scale(1.1)" : "scale(1)",
                      transition: "transform 0.15s",
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography sx={labelSx}>Tags</Typography>
              <Stack direction={"row"} gap={1}>
                <Box sx={{...controlSx, flex: 1}}>
                  <InputBase
                    placeholder="Add a tag..."
                    value={form.tagInput}
                    onChange={e => onChange("tagInput", e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    inputProps={{"aria-label": "Add tag"}}
                    sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                  />
                </Box>
                <Button
                  onClick={handleAddTag}
                  sx={{
                    textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
                    color: '#374151', bgcolor: '#FAFAFD', '&:hover': {bgcolor: '#ECECF3'},
                  }}
                >
                  Add
                </Button>
              </Stack>
              {form.tags.length > 0 && (
                <Stack direction={"row"} gap={0.75} flexWrap={"wrap"} sx={{mt: 1}}>
                  {form.tags.map(tag => (
                    <Chip
                      key={tag} label={tag} size={"small"}
                      onDelete={() => onChange("tags", form.tags.filter(t => t !== tag))}
                      sx={{fontSize: 11, bgcolor: 'rgba(132, 145, 247, 0.12)', color: '#4B55C9'}}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="row" gap={1.5} justifyContent="flex-end">
            <Button
              onClick={handleClose}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
                color: '#374151', bgcolor: '#FAFAFD', '&:hover': {bgcolor: '#ECECF3'},
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!form.name.trim() || !form.subProjectId || saving}
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={14} sx={{color: "#fff"}}/> : null}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
                bgcolor: '#374151', color: '#fff', '&:hover': {bgcolor: '#1F2937'},
                '&.Mui-disabled': {bgcolor: '#D1D5DB', color: '#9CA3AF'},
              }}
            >
              {saving ? "Creating..." : "Create Topic"}
            </Button>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CreateTopicModalNew;
